import { Controller, useForm } from 'react-hook-form';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '$hooks/index';
import {
  addPosition,
  companiesApi,
  companySlice,
  useSavePositionEnhancedMutation,
} from '$store/modules/companies';
import { useGetAllSmartInterviewsQuery } from '$store/modules/smart-interviews';
import SPKToast from '$components/SPKToast/SPKToast';
import {
  SuccessMessageEn,
  SuccessMessageEs,
  ErrorMessageEn,
  ErrorMessageEs,
} from '$constants/alerts';
import { userLanguage, ValidLanguages } from '$translations/index';
import PositionDto from '$types/serverTypes/positionDto';
import {
  buildPositionPayload,
  buildPositionFormData,
} from '../buildPositionPayload';

const SPKApplicationRequirements = ({
  onGoBack,
  onSuccess,
}: {
  onGoBack: () => void;
  onSuccess: () => void;
}) => {
  const [t] = useTranslation('global');
  const dispatch = useAppDispatch();
  const { employerAddPositionForm } = useAppSelector((state) => state.company);
  const { currencyToUsdConverter } = useAppSelector((state) => state.brand);
  const { data: smartInterviewsData = [] } = useGetAllSmartInterviewsQuery();
  const [savePositionEnhanced, { isLoading: isSaving }] =
    useSavePositionEnhancedMutation();

  const {
    IsPublic,
    EnableSmartInterviews,
    SmartInterviewQuestionId,
    ShowInterestedToEmployer,
  } = employerAddPositionForm;

  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      IsPublic: IsPublic ?? false,
      EnableSmartInterviews: EnableSmartInterviews ?? null,
      SmartInterviewQuestionId: SmartInterviewQuestionId ?? null,
      ShowInterestedToEmployer,
    },
  });

  const {
    EnableSmartInterviews: enableSmartInterviewsValue,
    IsPublic: isPublicValue,
    SmartInterviewQuestionId: smartInterviewQuestionIdValue,
    ShowInterestedToEmployer: showInterestedToEmployerValue,
  } = watch();

  const onSubmitHandler = async (formData: {
    IsPublic: boolean;
    EnableSmartInterviews: boolean | null;
    SmartInterviewQuestionId: number | null;
    ShowInterestedToEmployer: boolean;
  }) => {
    const { Company, DealId, TenantId, file } = employerAddPositionForm;

    const payload = buildPositionPayload(
      employerAddPositionForm,
      currencyToUsdConverter,
      {
        ...(Company && { Company }),
        ...(DealId && { DealId }),
        ...(TenantId && { TenantId }),
        IsPublic: formData.IsPublic,
        ...(formData.EnableSmartInterviews !== null && {
          EnableSmartInterviews: formData.EnableSmartInterviews,
        }),
        ...(formData.EnableSmartInterviews &&
          formData.SmartInterviewQuestionId && {
            SmartInterviewQuestionId: formData.SmartInterviewQuestionId,
          }),
        ShowInterestedToEmployer: formData.ShowInterestedToEmployer,
      }
    );

    const body = file ? buildPositionFormData(payload, file) : payload;

    const resp = await savePositionEnhanced(body as FormData);
    if ('data' in resp) {
      const newPosition: PositionDto = {
        ...resp.data,
        CandidatesPositionStatus: [],
      };
      dispatch(addPosition(newPosition as unknown as PositionDto));
      dispatch(companySlice.actions.resetEmployerAddPositionForm());
      dispatch(companiesApi.util.invalidateTags(['Company']));
      SPKToast(
        userLanguage === ValidLanguages.es
          ? SuccessMessageEs.SavePosition
          : SuccessMessageEn.SavePosition
      );
      onSuccess();
    } else {
      SPKToast(
        userLanguage === ValidLanguages.es
          ? ErrorMessageEs.ServerError
          : ErrorMessageEn.ServerError,
        { type: 'Error' }
      );
    }
  };

  const onErrorSubmitHandler = () => {
    SPKToast(t('EmployerAddPosition.Errors.MissingRequiredFields'), {
      type: 'Error',
    });
  };

  const goBackHandler = () => {
    dispatch(
      companySlice.actions.employerAddPositionOverview({
        ...employerAddPositionForm,
        EnableSmartInterviews: enableSmartInterviewsValue,
        IsPublic: isPublicValue,
        SmartInterviewQuestionId: smartInterviewQuestionIdValue,
        ShowInterestedToEmployer: showInterestedToEmployerValue,
      })
    );
    onGoBack();
  };

  return (
    <>
      <h1 className="text-4xl font-extrabold mb-8">Application Requirements</h1>
      <form
        onSubmit={handleSubmit(onSubmitHandler, onErrorSubmitHandler)}
        className="text-spk-mediumContrast text-xs w-full flex flex-col"
      >
        <fieldset className="flex flex-col gap-8" disabled={isSaving}>
          {/* Show on Job Board */}
          <div className="flex flex-col gap-2">
            <h3 className="text-base text-spk-highContrast font-medium">
              {t('EmployerAddPosition.PositionAdditional')}
            </h3>
            <Controller
              control={control}
              name="IsPublic"
              render={({ field }) => (
                <div className="flex flex-row items-center gap-2">
                  <Checkbox
                    inputId="IsPublic"
                    checked={field.value}
                    {...field}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                  <label
                    htmlFor="IsPublic"
                    className="text-sm text-spk-highContrast"
                  >
                    {t('EmployerAddPosition.IsPublic')}
                  </label>
                </div>
              )}
            />
            <Controller
              control={control}
              name="ShowInterestedToEmployer"
              render={({ field }) => (
                <div className="flex flex-row items-center gap-2">
                  <Checkbox
                    {...field}
                    checked={field.value}
                    inputId={field.name}
                    onChange={({ value }) => field.onChange(!value)}
                  />
                  <label
                    htmlFor={field.name}
                    className="text-sm text-spk-highContrast"
                  >
                    {t('EmployerAddPosition.ShowClientInterested')}
                  </label>
                </div>
              )}
            />
          </div>

          {/* Smart Interviews */}
          <div className="flex flex-col gap-2">
            <label className="text-base text-spk-highContrast font-medium">
              {t('EmployerAddPosition.SmartInterview')}
            </label>
            <Controller
              control={control}
              name="EnableSmartInterviews"
              render={({ field }) => (
                <SelectButton
                  {...field}
                  allowEmpty={false}
                  className="w-full my-1"
                  options={[
                    { label: t('common.yes'), value: true },
                    { label: 'No', value: false },
                  ]}
                />
              )}
            />
          </div>

          {/* Smart Interview ID (shown when enabled) */}
          {enableSmartInterviewsValue && (
            <div className="flex flex-col gap-1">
              <label
                htmlFor="SmartInterviewQuestionId"
                className="text-sm font-normal text-spk-mediumContrast"
              >
                {t('EmployerAddPosition.SmartInterview')} ID
              </label>
              <Controller
                control={control}
                name="SmartInterviewQuestionId"
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    inputId="SmartInterviewQuestionId"
                    className="w-full"
                    options={smartInterviewsData.map((si) => ({
                      label: `${si.Id} - ${si.Name}`,
                      value: si.Id,
                    }))}
                    placeholder={t(
                      'EmployerAddPosition.Placeholders.SelectAnOption'
                    )}
                    filter
                  />
                )}
              />
            </div>
          )}

          <div className="flex flex-row gap-8 w-full justify-center mt-8">
            <button
              type="button"
              onClick={goBackHandler}
              className="text-base text-employer-primary font-semibold"
            >
              {t('EmployerAddPosition.GoBack')}
            </button>
            <button
              type="submit"
              className="rounded-full bg-employer-primary px-4 py-2 !text-spk-softBlue text-base font-semibold"
            >
              Create Position
            </button>
          </div>
        </fieldset>
      </form>
    </>
  );
};

export default SPKApplicationRequirements;
